// src/components/AvatarMenu.jsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import Image from "next/image";
import { useAuth } from "@/context/AuthContext";
import { auth } from "@/firebase/config";
import { getAvatarUrl } from "@/lib/avatar";
import useDiscussionDrawerMode from "@/hooks/useDiscussionDrawerMode";
import { DISCUSSION_DRAWER_MODES } from "@/lib/discussionDrawerModes";
import { signOutNativeFirebase } from "@/lib/nativeFirebaseAuth";
import { usePortal } from "@/context/PortalContext";

/* click-outside helper */
function useClickOutside(refs, onOutside) {
  useEffect(() => {
    function handler(e) {
      const activeRefs = Array.isArray(refs) ? refs : [refs];
      if (!activeRefs.some((ref) => ref.current?.contains(e.target))) onOutside?.(e);
    }
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [refs, onOutside]);
}

/* theme helpers (default = light) */
function getInitialTheme() {
  if (typeof window === "undefined") return "light";
  const saved = localStorage.getItem("theme");
  if (saved === "light" || saved === "dark") return saved;
  const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  return prefersDark ? "dark" : "light";
}
function applyTheme(next) {
  document.documentElement.setAttribute("data-theme", next === "dark" ? "dark" : "light");
  localStorage.setItem("theme", next);
}

export default function AvatarMenu() {
  const portal = usePortal();
  const { user, profile, profileLoading } = useAuth();

  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const [theme, setTheme] = useState(getInitialTheme);
  const {
    mode: drawerMode,
    setMode: setDrawerMode,
    drawerToggleEnabled,
  } = useDiscussionDrawerMode();
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const avatarUrl = useMemo(() => getAvatarUrl({ profile, user, size: 128 }), [profile, user]);
  useClickOutside([rootRef, menuRef], () => setOpen(false));
  useEffect(() => {
    if (!portal) applyTheme(theme);
  }, [portal, theme]);

  useEffect(() => {
    if (!open) {
      setMenuPosition(null);
      return undefined;
    }

    const updateMenuPosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const gutter = 12;
      const menuWidth = 288;
      const top = rect.bottom + 8;
      setMenuPosition({
        top,
        left: Math.max(gutter, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - gutter)),
        maxHeight: Math.max(160, window.innerHeight - top - gutter),
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    window.visualViewport?.addEventListener("resize", updateMenuPosition);
    window.visualViewport?.addEventListener("scroll", updateMenuPosition);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
      window.visualViewport?.removeEventListener("resize", updateMenuPosition);
      window.visualViewport?.removeEventListener("scroll", updateMenuPosition);
    };
  }, [open]);

  if (!user) {
    return (
      <Link
        href="/login"
        className="inline-flex items-center rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm font-medium hover:shadow-sm focus-visible:outline-none focus-visible:ring-2"
      >
        Log in
      </Link>
    );
  }

  if (profileLoading) {
    return (
      <div
        className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[var(--border)] bg-[var(--surface-2)]"
        role="status"
        aria-label="Loading profile"
      >
        <span className="sr-only">Loading profile</span>
      </div>
    );
  }

  const handleLower =
    profile?.username_lowercase ||
    profile?.username?.toLowerCase?.() ||
    user?.displayName?.replace(/\s+/g, "").toLowerCase() ||
    user?.email?.split("@")[0] ||
    "member";
  const displayName = profile?.displayName || user?.displayName || "Member";

  const menu = open && menuPosition && typeof document !== "undefined"
    ? createPortal(
      <div
        ref={menuRef}
        role="menu"
        aria-label="Profile menu"
        tabIndex={-1}
        style={{ top: menuPosition.top, left: menuPosition.left, maxHeight: menuPosition.maxHeight }}
        className="fixed z-[100] w-72 overflow-x-hidden overflow-y-auto overscroll-contain rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] shadow-[var(--shadow-md)]"
      >
        {/* header */}
        <div className="flex items-center gap-3 border-b border-[var(--border)] p-3">
          <div className="relative h-9 w-9 overflow-hidden rounded-full">
            <Image src={avatarUrl} alt="" fill sizes="36px" className="object-cover" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-[var(--fg-primary)]">{displayName}</div>
            <div className="truncate text-xs text-[var(--fg-secondary)]">@{handleLower}</div>
          </div>
        </div>

        {/* sections */}
        <nav className="py-1">
          <SectionLabel>Profile</SectionLabel>
          <MenuLink href={`/u/${handleLower}`} onSelect={() => setOpen(false)}>View Profile</MenuLink>
          <MenuLink href="/profile" onSelect={() => setOpen(false)}>Edit Profile</MenuLink>
          <MenuLink href="/profile/password" onSelect={() => setOpen(false)}>Change Password</MenuLink>
          {!portal ? <MenuLink href="/profile/agents" onSelect={() => setOpen(false)}>My Agents</MenuLink> : null}

          <Separator />

          {portal ? (
            <ExternalMenuLink href={portal.publisherPrivacyUrl} onSelect={() => setOpen(false)}>
              Privacy
            </ExternalMenuLink>
          ) : (
            <>
              <SectionLabel>Info</SectionLabel>
              <MenuLink href="/info" onSelect={() => setOpen(false)}>Info</MenuLink>
              <MenuLink href="/info/privacy" onSelect={() => setOpen(false)}>Privacy</MenuLink>
            </>
          )}

          {!portal ? <Separator /> : null}

          {!portal && drawerToggleEnabled ? (
            <>
              <SectionLabel>Discussion drawer</SectionLabel>
              <div className="mx-2 mb-2 rounded-[calc(var(--radius)-8px)] border border-[var(--border)] p-1">
                <div className="flex items-stretch gap-1">
                  <PreferenceChip
                    label="Classic"
                    active={drawerMode === DISCUSSION_DRAWER_MODES.CLASSIC}
                    onClick={() => setDrawerMode(DISCUSSION_DRAWER_MODES.CLASSIC)}
                  />
                  <PreferenceChip
                    label="New"
                    active={drawerMode === DISCUSSION_DRAWER_MODES.BRANCH}
                    onClick={() => setDrawerMode(DISCUSSION_DRAWER_MODES.BRANCH)}
                  />
                </div>
              </div>

              <Separator />
            </>
          ) : null}

          {!portal ? (
            <>
              <SectionLabel>Theme</SectionLabel>
              <div className="mx-2 mb-2 rounded-[calc(var(--radius)-8px)] border border-[var(--border)] p-1">
                <div className="flex items-stretch gap-1">
                  <PreferenceChip label="Light" active={theme === "light"} onClick={() => setTheme("light")} />
                  <PreferenceChip label="Dark" active={theme === "dark"} onClick={() => setTheme("dark")} />
                </div>
              </div>
              <Separator />
            </>
          ) : null}

          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await signOutNativeFirebase();
              await auth.signOut();
            }}
            className="mx-1 mb-1 w-[calc(100%-0.5rem)] rounded-[calc(var(--radius)-8px)] px-3 py-2 text-left text-sm text-[var(--fg-primary)] hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2"
          >
            Sign Out
          </button>
        </nav>
      </div>,
      document.body
    )
    : null;

  return (
    <>
      <div className="relative z-[75]" ref={rootRef}>
      {/* trigger */}
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className="group relative inline-flex h-9 w-9 items-center justify-center overflow-hidden rounded-full ring-offset-2 focus-visible:outline-none focus-visible:ring-2"
        title="Open profile menu"
      >
        <span className="relative block h-full w-full">
          <Image src={avatarUrl} alt="Open profile menu" fill sizes="36px" className="object-cover" />
        </span>
      </button>

      </div>
      {menu}
    </>
  );
}

/* bits */
function SectionLabel({ children }) {
  return (
    <div className="px-3 pt-2 pb-1 text-sm font-semibold text-[var(--fg-primary)]">
      {children}
    </div>
  );
}
function Separator() {
  return <div role="separator" className="my-1 h-px bg-[var(--border)]" />;
}
function MenuLink({ href, children, onSelect }) {
  return (
    <Link
      href={href}
      role="menuitem"
      onClick={onSelect}
      className="mx-1 block rounded-[calc(var(--radius)-8px)] px-3 py-2 text-sm text-[var(--fg-primary)] hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2"
    >
      {children}
    </Link>
  );
}
function ExternalMenuLink({ href, children, onSelect }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      role="menuitem"
      onClick={onSelect}
      className="mx-1 block rounded-[calc(var(--radius)-8px)] px-3 py-2 text-sm text-[var(--fg-primary)] hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:ring-2"
    >
      {children}
    </a>
  );
}
function PreferenceChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={[
        "flex-1 rounded-md px-3 py-1.5 text-sm focus-visible:outline-none",
        active ? "bg-[var(--brand)] text-[var(--brand-foreground)]" : "text-[var(--fg-primary)] hover:bg-[var(--surface-2)]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}
